import PropTypes from 'prop-types';
import { memo, useState, useEffect } from 'react';
import UITour from 'ui-shepherd-react/tour/UITour';
import MultiTourHandler from 'ui-shepherd-react/lib/MultiTourHandler';
import OnboardingTourErrorBoundary from './OnboardingTourErrorBoundary';
import OnboardingTourSteps from './OnboardingTourSteps';
import { ONBOARDING_TOUR_SOURCE } from '../constants/URL';
import { OnboardingTourTrackingProvider } from './OnboardingTourTrackingProvider';
import { getTourById, getTourFromConfig } from '../util/getTour';
import { buildTourConfig } from '../util/tourConfig';
import { getLinkedTourState } from '../util/linkedTourUtils';
import { getTourIdFromQueryParams, getQueryParam, parseReturnUrl } from '../util/urlUtils';
import { localStorage } from '../util/storageUtil';
import { transformTour } from '../util/tourTransformer';
import { Metrics } from '../util/metrics';
import SentryManager from '../manager/SentryManager';
import { jsx as _jsx } from "react/jsx-runtime";
const SELENIUM_DISABLE_ONBOARDING_TOURS_KEY = 'selenium.disable.onboarding_tours';
const shouldDisableOnboardingTour = () => {
  return (
    // TODO: add link to selenium doc
    // Use the convention selenium.disable.<feature>.
    // selenium.disable.onboarding_tours = 'true' should prevent the tour from loading.
    localStorage.getItem(SELENIUM_DISABLE_ONBOARDING_TOURS_KEY) === 'true'
  );
};
const SELENIUM_ONBOARDING_TOUR_FLAG = 'selenium_onboarding_tour_enabled';
const SELENIUM_ONBOARDING_TOUR_CONFIG = 'selenium_onboarding_tour_config';
const setOnboardingTourConfigForSelenium = (tourId, tourData) => {
  if (localStorage.getItem(SELENIUM_ONBOARDING_TOUR_FLAG) === 'true') {
    localStorage.setItem(SELENIUM_ONBOARDING_TOUR_CONFIG, JSON.stringify(Object.assign({}, tourData, {
      tourId
    })));
  }
};
const defaultMultiTourHandler = new MultiTourHandler();
function OnboardingTour(props) {
  const {
    tourId: passedTourId,
    tour: localTour,
    multiTourHandler = defaultMultiTourHandler,
    navigationCallback
  } = props;
  const [activeTourId, setActiveTourId] = useState(null);
  const [tourState, setTourState] = useState({
    tourData: null,
    tourConfig: null
  });
  useEffect(() => {
    if (shouldDisableOnboardingTour()) {
      return;
    }
    const explicitTourId = passedTourId || getTourIdFromQueryParams();
    const linkedTourState = !explicitTourId ? getLinkedTourState() : null;
    const tourId = linkedTourState ? linkedTourState.tourId : explicitTourId;
    if (!tourId) {
      return;
    }
    const tourConfigPromise = localTour ? getTourFromConfig(tourId, localTour) : getTourById(tourId);
    tourConfigPromise.then(tourData_ => {
      const tourData = tourData_;
      setActiveTourId(tourId);
      tourData.steps = transformTour(tourData.steps);
      tourData.prevTourLength = (linkedTourState === null || linkedTourState === void 0 ? void 0 : linkedTourState.prevTourLength) || 0;
      tourData.linkedToursTotalStepCount = tourData.linkedToursTotalStepCount || (linkedTourState === null || linkedTourState === void 0 ? void 0 : linkedTourState.linkedToursTotalStepCount);
      tourData.returnUrl = (linkedTourState === null || linkedTourState === void 0 ? void 0 : linkedTourState.returnUrl) || parseReturnUrl(getQueryParam('returnUrl'));
      tourData.tourSource = (linkedTourState === null || linkedTourState === void 0 ? void 0 : linkedTourState.tourSource) || getQueryParam(ONBOARDING_TOUR_SOURCE) || undefined;
      const newTourConfig = buildTourConfig(tourId, tourData);
      setTourState({
        tourConfig: newTourConfig,
        tourData
      });
      setOnboardingTourConfigForSelenium(tourId, tourData);
    }).catch(error => {
      console.error('%c[onboarding-tours]', 'color: red;', error);
      SentryManager.reportLoadTourError(error, tourId);
      Metrics.counter('load-onboarding-tour-error', {
        tourId: tourId
      }).increment();
    });
  }, [passedTourId, localTour]);
  const {
    tourConfig,
    tourData
  } = tourState;
  if (!tourData || !tourConfig || !activeTourId) {
    return null;
  }
  return /*#__PURE__*/_jsx(OnboardingTourErrorBoundary, {
    tourId: activeTourId,
    children: /*#__PURE__*/_jsx(OnboardingTourTrackingProvider, {
      children: /*#__PURE__*/_jsx(UITour
      // @ts-expect-error _rootNode is not typed
      // eslint-disable-next-line react/prop-types
      , {
        _rootNode: props._rootNode,
        config: tourConfig,
        dataOnboarding: "onboarding-tour-container",
        multiTourHandler: multiTourHandler,
        navigationCallback: navigationCallback,
        tour: activeTourId,
        children: /*#__PURE__*/_jsx(OnboardingTourSteps, {
          tourData: tourData,
          tourId: activeTourId
        })
      })
    })
  });
}
OnboardingTour.propTypes = {
  tourId: PropTypes.string
};
export default /*#__PURE__*/memo(OnboardingTour);
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;